import {
  SourceStructureSelectionV1Schema,
  resolveOpenPencilSourceStructureSelection,
  type SourceStructureSelectionV1,
} from "./admission-v2.ts";
import { deriveSelectedComponentSetOpaqueIdsV2 } from "./component-ontology-candidate-v2.ts";
import { deriveSelectedDesignTokenCollectionOpaqueIdsV2 } from "./design-token-ontology-candidate-v2.ts";
import {
  validateOpenPencilFactShardManifestV2,
  verifyOpenPencilStructuralProjection,
  type OpaqueId,
  type OpenPencilFactShardManifestV2,
  type OpenPencilStructuralFactShardV1,
} from "./fact-shards.ts";

export interface FixedWave1OpaqueCandidateSelectionV2 {
  allowedCollectionOpaqueIds: OpaqueId[];
  allowedComponentSetOpaqueIds: OpaqueId[];
}

export interface FoundationComponentWave1AuthorizedSelectionEvaluationV2 {
  passed: boolean;
  issues: Array<
    "selection-invalid" | "selection-unresolved" | "selection-profile-invalid"
  >;
}

type JsonRecord = Record<string, unknown>;

function deepFreeze<T>(value: T, seen = new WeakSet<object>()): T {
  if (
    value === null ||
    typeof value !== "object" ||
    ArrayBuffer.isView(value) ||
    seen.has(value)
  ) {
    return value;
  }
  seen.add(value);
  for (const child of Object.values(value as JsonRecord)) {
    deepFreeze(child, seen);
  }
  return Object.freeze(value);
}

function snapshotStructuralInput(input: {
  projectionKey: Uint8Array;
  structuralManifest: OpenPencilFactShardManifestV2;
  structuralBodies: OpenPencilStructuralFactShardV1[];
}) {
  return {
    projectionKey: input.projectionKey.slice(),
    structuralManifest: deepFreeze(structuredClone(input.structuralManifest)),
    structuralBodies: deepFreeze(structuredClone(input.structuralBodies)),
  };
}

function snapshotReadbackAndSelection(input: {
  readback: unknown;
  selection: SourceStructureSelectionV1;
}) {
  return {
    readback: deepFreeze(structuredClone(input.readback)),
    selection: deepFreeze(structuredClone(input.selection)),
  };
}

function isVariableCollectionPath(path: readonly string[]): boolean {
  return (
    path.length === 3 && path[0] === "variables" && path[1] === "collections"
  );
}

function isComponentSetPath(path: readonly string[]): boolean {
  return path.length === 3 && path[1] === "componentSets";
}

export function evaluateFoundationComponentWave1AuthorizedSelectionV2(
  readback: unknown,
  selectionInput: unknown,
): FoundationComponentWave1AuthorizedSelectionEvaluationV2 {
  const selection = SourceStructureSelectionV1Schema.safeParse(selectionInput);
  if (!selection.success) {
    return { passed: false, issues: ["selection-invalid"] };
  }
  if (
    !resolveOpenPencilSourceStructureSelection(
      readback as Parameters<
        typeof resolveOpenPencilSourceStructureSelection
      >[0],
      selection.data,
    ).passed
  ) {
    return { passed: false, issues: ["selection-unresolved"] };
  }
  if (
    !selection.data.members.some((member) =>
      isVariableCollectionPath(member.structuralPath),
    ) ||
    !selection.data.members.some((member) =>
      isComponentSetPath(member.structuralPath),
    )
  ) {
    return { passed: false, issues: ["selection-profile-invalid"] };
  }
  return { passed: true, issues: [] };
}

function bodyMap(
  manifest: OpenPencilFactShardManifestV2,
  bodies: readonly OpenPencilStructuralFactShardV1[],
): Map<string, OpenPencilStructuralFactShardV1> {
  if (bodies.length !== manifest.shards.length) {
    throw new Error("FIXED_WAVE1_STRUCTURAL_PROJECTION_INVALID");
  }
  const result = new Map<string, OpenPencilStructuralFactShardV1>();
  for (const shard of manifest.shards) {
    const matches = bodies.filter((body) => body.kind === shard.kind);
    if (matches.length !== 1 || result.has(shard.artifactRef)) {
      throw new Error("FIXED_WAVE1_STRUCTURAL_PROJECTION_INVALID");
    }
    result.set(shard.artifactRef, matches[0]!);
  }
  return result;
}

export async function deriveFixedWave1OpaqueCandidateSelectionV2(input: {
  readback: unknown;
  selection: SourceStructureSelectionV1;
  projectionKey: Uint8Array;
  structuralManifest: OpenPencilFactShardManifestV2;
  structuralBodies: OpenPencilStructuralFactShardV1[];
}): Promise<FixedWave1OpaqueCandidateSelectionV2> {
  const structuralSnapshot = snapshotStructuralInput({
    projectionKey: input.projectionKey,
    structuralManifest: input.structuralManifest,
    structuralBodies: input.structuralBodies,
  });
  const bodies = bodyMap(
    structuralSnapshot.structuralManifest,
    structuralSnapshot.structuralBodies,
  );
  if (
    validateOpenPencilFactShardManifestV2(
      structuralSnapshot.structuralManifest,
      bodies,
    ).length > 0
  ) {
    throw new Error("FIXED_WAVE1_STRUCTURAL_PROJECTION_INVALID");
  }
  const readback = input.readback;
  const originalProjection = verifyOpenPencilStructuralProjection(
    readback,
    structuralSnapshot.projectionKey,
    structuralSnapshot.structuralManifest,
    bodies,
  );
  if (!originalProjection.valid) {
    throw new Error(
      `FIXED_WAVE1_STRUCTURAL_PROJECTION_INVALID:${originalProjection.issues.join(",")}`,
    );
  }
  const selection = input.selection;
  const snapshot = {
    ...structuralSnapshot,
    ...snapshotReadbackAndSelection({ readback, selection }),
  };
  const frozenProjection = verifyOpenPencilStructuralProjection(
    snapshot.readback,
    snapshot.projectionKey,
    snapshot.structuralManifest,
    bodies,
  );
  if (!frozenProjection.valid) {
    throw new Error(
      `FIXED_WAVE1_STRUCTURAL_PROJECTION_INVALID:${frozenProjection.issues.join(",")}`,
    );
  }
  const authorization = evaluateFoundationComponentWave1AuthorizedSelectionV2(
    snapshot.readback,
    snapshot.selection,
  );
  if (!authorization.passed) {
    throw new Error(
      `FIXED_WAVE1_SELECTION_INVALID:${authorization.issues.join(",")}`,
    );
  }
  const structuralFacts = snapshot.structuralBodies.flatMap(
    (body) => body.facts,
  );
  const allowedCollectionOpaqueIds =
    await deriveSelectedDesignTokenCollectionOpaqueIdsV2({
      readback: snapshot.readback,
      selection: snapshot.selection,
      projectionKey: snapshot.projectionKey,
      structuralFacts,
    });
  const allowedComponentSetOpaqueIds =
    await deriveSelectedComponentSetOpaqueIdsV2({
      readback: snapshot.readback,
      selection: snapshot.selection,
      projectionKey: snapshot.projectionKey,
      structuralFacts,
    });
  if (
    allowedCollectionOpaqueIds.length === 0 ||
    allowedComponentSetOpaqueIds.length === 0
  ) {
    throw new Error("FIXED_WAVE1_SELECTION_PROFILE_INVALID");
  }
  return {
    allowedCollectionOpaqueIds: [...new Set(allowedCollectionOpaqueIds)].sort(),
    allowedComponentSetOpaqueIds: [
      ...new Set(allowedComponentSetOpaqueIds),
    ].sort(),
  };
}
